import { Injectable } from '@angular/core';
import {
    IChecklist2ReportDateRange,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
    IChecklistV2ReportParameterSettings,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { guid } from '@fi-shared/utils/guid';
import { FiChecklist2ReportTimePeriodType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-time-period-type';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

@Injectable()
export class FiChecklist2ReportDataService {
    private readonly debounceIntervalMs = 100;

    private dateRanges: {
        [reportId: number]: IChecklist2ReportDateRange;
    } = {};
    private loadings$: {
        [reportId: number]: BehaviorSubject<boolean>;
    };
    private reportParameterSettings: {
        [reportId: number]: IChecklistV2ReportParameterSettings;
    } = {};

    constructor() {}

    reset() {
        this.dateRanges = {};
        this.loadings$ = {};
        this.reportParameterSettings = {};
    }

    private _dateRangeChanged$ = new Subject<number>();
    subscribeOnDateRangeChanged(reportId: number, ngOnDestroy$: Observable<void>, callback: () => void) {
        this._dateRangeChanged$
            .pipe(
                takeUntil(ngOnDestroy$),
                filter((id) => reportId === id),
            )
            .subscribe((reportId) => {
                callback();
            });
    }

    setNewDateRange(reportId: number, dateRange: IChecklist2ReportDateRange): void {
        this.dateRanges[reportId] = dateRange;
        this._dateRangeChanged$.next(reportId);
        this._filterOrDateRangeChanged$.next(reportId);
    }

    //#endregion

    //#region additional data (checklist unit details, performance benchmark etc) update routine
    private _filterOrDateRangeChanged$ = new Subject<number>();
    triggerFilterChanged(reportId: number) {
        this._filterOrDateRangeChanged$.next(reportId);
    }
    subscribeOnFilterOrDateRangeChanged(reportId: number, ngOnDestroy$: Observable<void>, callback: () => void) {
        this._filterOrDateRangeChanged$
            .pipe(
                takeUntil(ngOnDestroy$),
                filter((id) => reportId === id),
                debounceTime(this.debounceIntervalMs),
            )
            .subscribe((reportId) => {
                callback();
            });
    }
    //#endregion

    //#region helper methods

    getRecentHistoryDateRange(): IChecklist2ReportDateRange {
        return { TimePeriodType: FiChecklist2ReportTimePeriodType.Last24hrs };
    }

    getDateRange(reportId: number): IChecklist2ReportDateRange {
        return (
            this.dateRanges[reportId] ??
            <IChecklist2ReportDateRange>{
                TimePeriodType: FiChecklist2ReportTimePeriodType.LastWeek,
            }
        );
    }

    resetDateRange(reportId: number) {
        delete this.dateRanges[reportId];
    }

    getWidgetFilter(reportParameter: IChecklistV2ReportParameter): IChecklistV2ReportParameterFilter {
        const defaultFilter: IChecklistV2ReportParameterFilter = reportParameter.Settings.Filters?.find(
            (filter: IChecklistV2ReportParameterFilter) => {
                return filter.Guid === reportParameter.Settings.CurrentFilterGuid;
            },
        );
        const currentFilter: IChecklistV2ReportParameterFilter = isDefined(defaultFilter)
            ? defaultFilter
            : { Guid: guid() };
        return currentFilter;
    }

    resetWidgetFilter(reportParam: IChecklistV2ReportParameter) {
        if (!this.reportParameterSettings[reportParam.Id]) {
            // save current settings
            this.reportParameterSettings[reportParam.Id] = cloneDeep(reportParam.Settings);
        } else {
            reportParam.Settings = cloneDeep(this.reportParameterSettings[reportParam.Id]);
        }
    }

    setWidgetFilter(reportParam: IChecklistV2ReportParameter) {
        this.reportParameterSettings[reportParam.Id] = cloneDeep(reportParam.Settings);
    }

    getLoading$(reportId: number): Observable<boolean> {
        return this._getLoading$(reportId).asObservable();
    }

    private _getLoading$(reportId: number): BehaviorSubject<boolean> {
        if (!this.loadings$[reportId]) {
            this.loadings$[reportId] = new BehaviorSubject<boolean>(false);
        }
        return this.loadings$[reportId];
    }
    //#endregion
}
